import { Fragment } from "react";
import {
  computeInvoiceDueDate,
  formatFacturaMoney,
  invoiceLineMath,
  summarizeInvoiceVat,
  type InvoiceDetailDto,
} from "@tc/shared";
import { LogoIcon } from "@/components/ui";

/**
 * Read-only structured factura (TW-3527) — a faithful on-screen rendering of the supplied TC design:
 * issuer masthead, "FACTURA" title, client block, the bilingual meta strip, the grouped line-items
 * table (per-line Sin IVA / IVA / con IVA), the per-rate VAT summary, the grand-total bar and the
 * payment block. The labels are **fixed bilingual EN/ES** (the design + the PDF are fixed bilingual,
 * regardless of the viewer's language), so they live as literals here. The per-line maths and the VAT
 * summary use the shared pure helpers, so the screen, the PDF and the server agree to the cent.
 *
 * Money renders Spanish-style ("1.234,56 €") to match the printed factura. For a Draft the issuer +
 * client render live (from current settings / the club's active billing); once issued they are the
 * frozen snapshot the detail endpoint returns — this component just renders whatever it is given.
 */
export function FacturaView({ invoice }: { invoice: InvoiceDetailDto }) {
  const totals = summarizeInvoiceVat(invoice.lineItems);
  const issuer = invoice.issuer;
  const client = invoice.client;
  const money = (n: number) => eur(n, invoice.currency);

  return (
    <div className="factura">
      <div className="factura-sheet">
        {/* Header — title top-left, issuer block top-right (right-aligned) */}
        <div className="factura-header">
          <div className="factura-header-left">
            <div className="factura-title">{invoice.status === "Draft" ? "FACTURA PRO-FORMA" : "FACTURA"}</div>
            {invoice.status === "Draft" ? (
              <div className="factura-proforma">Pro-forma · No es una factura definitiva</div>
            ) : null}
            <div className={invoice.status === "Draft" ? "factura-rule" : "factura-rule factura-rule-final"} />
          </div>
          <div className="factura-mast">
            <LogoIcon className="factura-logo" />
            <div className="factura-mast-text">
              <div className="factura-issuer-name">{issuer?.companyName || "—"}</div>
              {oneLine(issuer?.address) ? <div className="factura-issuer-line">{oneLine(issuer?.address)}</div> : null}
              {issuer?.phone ? <div className="factura-issuer-line">Tel: {issuer.phone}</div> : null}
              {issuer?.taxNo ? <div className="factura-issuer-line">CIF: {issuer.taxNo}</div> : null}
            </div>
          </div>
        </div>

        {/* Client — the party being billed */}
        <div className="factura-client">
          <div className="factura-cap">Company Details / Datos Entidad</div>
          <div className="factura-client-name">{client?.companyName || "—"}</div>
          {clientLines(client).map((l, i) => (
            <div key={i} className="factura-client-line">
              {l}
            </div>
          ))}
        </div>

        {/* Meta strip */}
        <table className="factura-meta">
          <thead>
            <tr>
              <th>{bi("Date", "Fecha")}</th>
              <th>{bi("Number", "Número")}</th>
              <th>{bi("Reference", "Referencia")}</th>
              <th>{bi("Reservation dates", "Fechas reserva")}</th>
              <th>{bi("Reference", "Referencia")}</th>
              <th>{bi("Due date", "Vencimiento")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{isoDate(invoice.issueDate ?? invoice.issuedAt) || "—"}</td>
              <td>{invoice.number}</td>
              <td>{invoice.tripReference}</td>
              <td>{reservationDates(invoice)}</td>
              <td>{invoice.sport || "—"}</td>
              <td>{isoDate(dueDateOf(invoice)) || "—"}</td>
            </tr>
          </tbody>
        </table>

        {/* Line items */}
        <table className="factura-items">
          <thead>
            <tr>
              <th className="l">{bi("Details", "Conceptos")}</th>
              <th className="c">{bi("Un.", " ")}</th>
              <th>{bi("Price", "Precio")}</th>
              <th>{bi("Total", "Sin IVA")}</th>
              <th className="c">{bi("IVA", "%")}</th>
              <th>{bi("IVA", "Importe")}</th>
              <th>{bi("Subtotal", "con IVA")}</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems.length === 0 ? (
              <tr>
                <td className="l" colSpan={7} style={{ color: "var(--fac-muted)" }}>
                  —
                </td>
              </tr>
            ) : (
              invoice.lineItems.map((line, i) => {
                const prev = invoice.lineItems[i - 1];
                const m = invoiceLineMath(line);
                return (
                  <Fragment key={line.id}>
                    {line.groupTitle && line.groupTitle !== prev?.groupTitle ? (
                      <tr className="group">
                        <td colSpan={7}>{line.groupTitle}</td>
                      </tr>
                    ) : null}
                    {line.section && line.section !== prev?.section ? (
                      <tr className="section">
                        <td colSpan={7}>{line.section}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <td className="l">
                        {line.description}
                        {line.lineDate ? <span className="factura-items date"> · {line.lineDate}</span> : null}
                      </td>
                      <td className="c">{trimNum(line.qty)}</td>
                      <td>{money(line.unitPrice)}</td>
                      <td>{money(m.ex)}</td>
                      <td className="c">{line.vatRate}%</td>
                      <td>{money(m.iva)}</td>
                      <td>{money(m.inc)}</td>
                    </tr>
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>

        {/* VAT summary */}
        <div className="factura-lower">
          <table className="factura-vat">
            <thead>
              <tr>
                <th className="l">Base Imponible</th>
                <th>Tipo</th>
                <th>IVA</th>
              </tr>
            </thead>
            <tbody>
              {totals.rates.map((r) => (
                <tr key={r.rate}>
                  <td>{money(r.base)}</td>
                  <td>{r.rate}%</td>
                  <td>{money(r.iva)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Total */}
        <div className="factura-total">
          <span className="tl">Total Amount · Importe total</span>
          <span className="tv">{money(totals.grandTotal)}</span>
        </div>

        {/* Payment block */}
        {issuer && (issuer.paymentTerms || issuer.paymentInstructions) ? (
          <div className="factura-pay">
            <div className="factura-pay-cap">Payment / Pago</div>
            {issuer.paymentTerms ? <div className="factura-pay-line">{issuer.paymentTerms}</div> : null}
            {issuer.paymentInstructions
              ? issuer.paymentInstructions.split("\n").map((l, i) => (
                  <div key={i} className="factura-pay-line">
                    {l}
                  </div>
                ))
              : null}
          </div>
        ) : null}

      </div>
    </div>
  );
}

/** A bilingual stacked header cell (English over italic Spanish), matching the design. */
function bi(en: string, es: string) {
  return (
    <>
      <span className="h-en">{en}</span>
      <span className="h-es">{es}</span>
    </>
  );
}

/** The client block: the address on one line, then the NIF on its own line. */
function clientLines(client: InvoiceDetailDto["client"]): string[] {
  if (!client) return [];
  const cityLine = [client.postalCode, client.city].filter(Boolean).join(" ").trim();
  const address = [client.street, cityLine || null, client.province].filter(Boolean).join(", ");
  return [address, client.taxId ? `NIF: ${client.taxId}` : ""].filter((l): l is string => Boolean(l && l.trim()));
}

/** Collapse a multi-line address into one comma-separated line. */
function oneLine(value: string | null | undefined): string {
  return (value ?? "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)
    .join(", ");
}

/** Spanish-style money: "1.234,56 €" — the shared factura formatter (independent of viewer locale). */
export function eur(n: number, currency = "EUR"): string {
  return formatFacturaMoney(n, currency);
}

/** Trim a quantity to a plain number (drop trailing .00). */
function trimNum(n: number): string {
  return String(n);
}

/**
 * The Vencimiento to print: the stamped due date once issued; on a Draft (pro-forma) a live preview
 * computed from the payment terms — issue date (or today) + due days (TRAV-2). A Draft's *stored*
 * due date (a pre-terms leftover typed before the terms select existed) is deliberately ignored:
 * issue recomputes it from the terms, so the pro-forma always shows exactly what issue will stamp.
 */
function dueDateOf(invoice: InvoiceDetailDto): string | null {
  if (invoice.status !== "Draft") return invoice.dueDate;
  return computeInvoiceDueDate(invoice.issueDate ?? new Date().toISOString(), invoice.dueDays);
}

/** ISO timestamp → "DD/MM/YYYY", or "" when null. */
function isoDate(iso: string | null): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

/** The reservation-dates cell: travel start–end as DD/MM/YYYY, collapsed when equal/absent. */
function reservationDates(invoice: InvoiceDetailDto): string {
  const start = isoDate(invoice.travelStartDate);
  const end = isoDate(invoice.travelEndDate);
  if (!start && !end) return "—";
  if (start && end && start !== end) return `${start}–${end}`;
  return start || end;
}
